import i18next from 'i18next';
import { logToSentry } from './sentry';

/**
 * Tipos de error que pueden ocurrir en las llamadas a la API
 */
export const ERROR_TYPES = {
  NOT_FOUND: 'not_found',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  SERVER_ERROR: 'server_error',
  NETWORK_ERROR: 'network_error',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
};

/**
 * Maneja errores de API de forma centralizada, distinguiendo entre diferentes tipos de errores
 * y tomando acciones apropiadas según el tipo.
 *
 * @param {Object} response - Respuesta de la API (puede ser error o respuesta normal)
 * @param {Object} routerOrHistory - Objeto router de React Router con history, o objeto con history, o history directamente
 * @param {Object} options - Opciones adicionales
 * @param {Function} options.showNotification - Función para mostrar notificaciones
 * @param {Object} options.user - Información del usuario para logging
 * @param {Object} options.context - Contexto adicional para logging (ej: { courseGuid, lessonGuid })
 * @returns {Object} Información del error: { type, canRetry, status, message }
 */
export function handleApiError(response, routerOrHistory, options = {}) {
  const { showNotification, user, context = {}, t } = options;

  // Usar función de traducción proporcionada o i18next directamente
  const translate = t || ((key) => i18next.t(key));

  // Normalizar router/history para obtener el método push
  let history;
  if (routerOrHistory?.history) {
    history = routerOrHistory.history;
  } else if (routerOrHistory?.push) {
    history = routerOrHistory;
  } else if (routerOrHistory) {
    history = routerOrHistory;
  }

  // Si no hay respuesta, es un error de red
  if (!response) {
    const errorInfo = {
      type: ERROR_TYPES.NETWORK_ERROR,
      canRetry: true,
      status: null,
      message: translate('common:Connection error. Please check your internet connection.'),
    };

    logToSentry('warning', 'Network error in API call', {
      user,
      tags: { error_type: ERROR_TYPES.NETWORK_ERROR, ...context },
      extra: { ...context, timestamp: new Date().toISOString() },
    });

    return errorInfo;
  }

  // Si la respuesta tiene un objeto 'error', es un error de axios
  // El interceptor de axios devuelve { error: error.response }
  const errorResponse = response.error || response;
  const status = errorResponse?.status || response?.status;

  // Si no hay status, es un error desconocido
  if (!status) {
    const errorInfo = {
      type: ERROR_TYPES.UNKNOWN,
      canRetry: true,
      status: null,
      message: translate('common:An unexpected error occurred. Please try again.'),
    };

    logToSentry('error', 'Unknown error in API call', {
      user,
      tags: { error_type: ERROR_TYPES.UNKNOWN, ...context },
      extra: { response, ...context, timestamp: new Date().toISOString() },
    });

    return errorInfo;
  }

  // Manejar diferentes códigos de estado HTTP
  let errorInfo;

  switch (status) {
    case 404:
      // Recurso no encontrado - redirigir a /not-found
      errorInfo = {
        type: ERROR_TYPES.NOT_FOUND,
        canRetry: false,
        status: 404,
        message: translate('common:The requested resource was not found.'),
      };

      logToSentry('warning', 'Resource not found (404)', {
        user,
        tags: { error_type: ERROR_TYPES.NOT_FOUND, http_status: 404, ...context },
        extra: { ...context, timestamp: new Date().toISOString() },
      });

      if (history?.push) {
        history.push('/not-found');
      }
      break;

    case 401:
      // No autorizado - token expirado o inválido
      errorInfo = {
        type: ERROR_TYPES.UNAUTHORIZED,
        canRetry: false,
        status: 401,
        message: translate('common:Your session has expired. Please log in again.'),
      };

      logToSentry('warning', 'Unauthorized access (401)', {
        user,
        tags: { error_type: ERROR_TYPES.UNAUTHORIZED, http_status: 401, ...context },
        extra: { ...context, timestamp: new Date().toISOString() },
      });

      // Redirigir a login
      if (history?.push) {
        history.push('/auth/login');
      }
      break;

    case 403:
      // Prohibido - sin permisos
      errorInfo = {
        type: ERROR_TYPES.FORBIDDEN,
        canRetry: false,
        status: 403,
        message: translate('common:You do not have permission to access this resource.'),
      };

      logToSentry('warning', 'Forbidden access (403)', {
        user,
        tags: { error_type: ERROR_TYPES.FORBIDDEN, http_status: 403, ...context },
        extra: { ...context, timestamp: new Date().toISOString() },
      });

      if (history?.push) {
        history.push('/not-found');
      }
      break;

    case 500:
    case 502:
    case 503:
      // Errores del servidor - temporales, permitir retry
      errorInfo = {
        type: ERROR_TYPES.SERVER_ERROR,
        canRetry: true,
        status: status,
        message: translate('common:The server is experiencing problems. Please try again in a few moments.'),
      };

      logToSentry('error', `Server error (${status})`, {
        user,
        tags: { error_type: ERROR_TYPES.SERVER_ERROR, http_status: status, ...context },
        extra: { ...context, timestamp: new Date().toISOString() },
        error: new Error(`Server error ${status}`),
      });

      break;

    default:
      // Otros errores HTTP
      errorInfo = {
        type: ERROR_TYPES.UNKNOWN,
        canRetry: true,
        status: status,
        message: translate('common:An error occurred. Please try again.'),
      };

      logToSentry('warning', `Unexpected HTTP error (${status})`, {
        user,
        tags: { error_type: ERROR_TYPES.UNKNOWN, http_status: status, ...context },
        extra: { ...context, timestamp: new Date().toISOString() },
      });
  }

  return errorInfo;
}

/**
 * Verifica si una respuesta es un error basándose en su estructura
 *
 * @param {Object} response - Respuesta de la API
 * @returns {boolean} true si es un error
 */
export function isErrorResponse(response) {
  if (!response) return true;
  if (response.error) return true;
  if (response.status && response.status >= 400) return true;
  return false;
}
